/*!
 * Theme switcher for IAP v3
 * Based on Bootstrap's color mode toggler (https://getbootstrap.com/)
 *
 * Supports named themes via two data attributes on <html>:
 * - data-bs-theme="light|dark" — Bootstrap base mode
 * - data-iap-theme="custom" — triggers component overrides for runtime themes
 *
 * Theme colors are applied at runtime via inline CSS custom properties.
 * See accountsettings.ts for theme definitions and application logic.
 * On page load, stored themeVars are re-applied from localStorage.
 */

(() => {
    'use strict'

    const resolveAutoBase = () =>
        window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

    const getStoredTheme = () => localStorage.getItem('theme')

    const getPreferredTheme = () => getStoredTheme() || 'auto'

    const applyThemeVisual = theme => {
        let bsTheme
        if (theme === 'auto') {
            bsTheme = resolveAutoBase()
        } else if (theme === 'light' || theme === 'dark') {
            bsTheme = theme
        } else {
            // Runtime theme — read stored base mode (light/dark)
            const storedBase = localStorage.getItem('themeBase')
            bsTheme = storedBase || 'dark'
        }

        document.documentElement.setAttribute('data-bs-theme', bsTheme)

        // Runtime themes get the 'custom' overlay for component overrides
        if (theme !== 'auto' && theme !== 'light' && theme !== 'dark') {
            document.documentElement.setAttribute('data-iap-theme', 'custom')
        } else {
            document.documentElement.removeAttribute('data-iap-theme')
        }
    }

    const setTheme = theme => {
        localStorage.setItem('theme', theme)
        applyThemeVisual(theme)
    }

    // Expose to window for account settings and other pages
    window['setTheme'] = setTheme
    window['getTheme'] = getPreferredTheme
    window['applyThemeVisual'] = applyThemeVisual

    // Apply theme before first paint
    setTheme(getPreferredTheme())

    // Reapply inline CSS vars for runtime themes (stored by account settings)
    const storedVars = localStorage.getItem('themeVars')
    if (storedVars) {
        try {
            const vars = JSON.parse(storedVars)
            document.documentElement.setAttribute('data-iap-theme', 'custom')
            for (const [key, value] of Object.entries(vars)) {
                document.documentElement.style.setProperty(key, /** @type {string} */ (value))
            }
        } catch (_) { /* ignore corrupt data */ }
    }

    // Re-evaluate when system preference changes (only affects "auto" mode)
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        const stored = getStoredTheme()
        if (!stored || stored === 'auto') {
            setTheme('auto')
        }
    })
})()
